import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { Button, Card, Icon } from '../ui/index.js';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Catches render errors anywhere below it and shows a friendly card instead of
 * a blank screen or a stack trace. Wraps the whole app and each route section.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('A screen ran into a problem', error, info);
  }

  private readonly reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="pt-shell__main">
        <Card>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 'var(--pt-space-3)',
              padding: 'var(--pt-space-5)',
            }}
          >
            <span style={{ color: 'var(--pt-warn)' }}>
              <Icon name="alert" size={44} title="Something went wrong" />
            </span>
            <h2>Something went wrong on our side</h2>
            <p style={{ color: 'var(--pt-text-muted)', maxWidth: '42ch' }}>
              It is not your fault. Your work is saved. You can try again, or reload the page.
            </p>
            <div style={{ display: 'flex', gap: 'var(--pt-space-3)', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button onClick={this.reset}>Try again</Button>
              <Button variant="secondary" onClick={() => window.location.reload()}>
                Reload the page
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}
